/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createAnalyticsStore } from 'src/luis/core/stores/analyticsStore';
import { AnalyticsStatus, AnalyticsStatusOptions } from 'src/luis/projects/analytics/types/AnalyticsStatus';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { IntentTrainingResult } from 'src/luis/projects/analytics/types/IntentTrainingResult';
import { AnalyticsStore } from 'src/luis/projects/analytics/types/stores/AnalyticsStore';
import { AnalyticsStatusSwagger } from 'src/luis/projects/analytics/types/swagger/AnalyticsStatusSwagger';
import { AnalyticsSwagger } from 'src/luis/projects/analytics/types/swagger/AnalyticsSwagger';
import { EndpointHitsSwagger } from 'src/luis/projects/analytics/types/swagger/EndpointHitsSwagger';
import { IntentFaultyUtterancesSwagger } from 'src/luis/projects/analytics/types/swagger/IntentFaultyUtterancesSwagger';
import { TrainingResultMetadata } from 'src/luis/projects/analytics/types/TrainingResultMetadata';
import { UtteranceTrainingResult } from 'src/luis/projects/analytics/types/UtteranceTrainingResult';
import { LuisId } from 'src/luis/types/Resource';

/**
 * Maps the api received analytics object to an analytics store item.
 *
 * @param analyticsItem The analytics object received from the api.
 * @returns An analytics store item.
 */
export const analyticsSwaggerToAnalyticsStore = (analyticsItem: Partial<AnalyticsSwagger>): AnalyticsStore =>
    createAnalyticsStore({
        ...analyticsMetadataSwaggerToTrainingResultMetadata(analyticsItem),
        intentsTrainingResult: new Map<LuisId, IntentTrainingResult>()
    });

/**
 * Maps the api received analytics metadata object to a TrainingResultMetadata item.
 *
 * @param analyticsItem The analytics metadata object received from the api.
 * @returns A TrainingResultMetadata item.
 */
export const analyticsMetadataSwaggerToTrainingResultMetadata = (analyticsItem: Partial<AnalyticsSwagger>): TrainingResultMetadata => {
    const incorrectUtterancesCount = analyticsItem.misclassifiedUtterancesCount;
    const unclearUtterancesCount = analyticsItem.ambiguousUtterancesCount - analyticsItem.misclassifiedAmbiguousUtterancesCount;
    const correctUtterancesCount = analyticsItem.appVersionUtterancesCount - incorrectUtterancesCount - unclearUtterancesCount;

    const utterancesPerIntent = new Map<LuisId, IntentMetadata>();
    if (analyticsItem.modelsMetadata) {
        analyticsItem.modelsMetadata.forEach(intent => {
            const total = intent.utterancesCount;
            const incorrect = intent.misclassifiedUtterancesCount;
            const unclear = intent.ambiguousUtterancesCount - intent.misclassifiedAmbiguousUtterancesCount;
            const correct = total - incorrect - unclear;

            utterancesPerIntent.set(intent.modelName, {
                numUtterances: total,
                intentId: intent.modelId,
                intentName: intent.modelName,
                numCorrectUtterances: correct,
                numUnclearUtterances: unclear,
                numIncorrectUtterances: incorrect,
                correctAccuracy: total === 0 ? 0 : (correct * 100) / total,
                unclearAccuracy: total === 0 ? 0 : (unclear * 100) / total,
                incorrectAccuracy: total === 0 ? 0 : (incorrect * 100) / total
            });
        });
    }

    return {
        intentsCount: analyticsItem.intentsCount,
        correctUtterancesCount,
        unclearUtterancesCount,
        entitiesCount: analyticsItem.entitiesCount,
        patternsCount: analyticsItem.patternsCount,
        utterancesPerIntent,
        incorrectUtterancesCount,
        phraseListsCount: analyticsItem.phraseListsCount,
        utterancesCount: analyticsItem.appVersionUtterancesCount,
        lastTrainingDate: analyticsItem.lastTrainDate ? new Date(analyticsItem.lastTrainDate) : null,
        unclearIncorrectUtterancesCount: analyticsItem.misclassifiedAmbiguousUtterancesCount,
        timeElapsed: analyticsItem.trainingTime ? new Date(`1234-12-12T${analyticsItem.trainingTime}`) : null
    };
};

/**
 * Maps the api received analytics operation status object to an AnalyticsStatus item.
 *
 * @param statusItem The operation status object received from the api.
 * @returns An AnalyticsStatus item.
 */
export const analyticsStatusSwaggerToAnalyticsStatus = (statusItem: AnalyticsStatusSwagger): AnalyticsStatus => {
    let status: AnalyticsStatusOptions = 'notStarted';
    switch (statusItem.status) {
        case 1:
            status = 'running';
            break;
        case 2:
            status = 'completed';
            break;
        case 3:
            status = 'failed';
            break;
        case 0:
        default:
            status = 'notStarted';
            break;
    }

    return {
        status,
        operationId: statusItem.operationId,
        operationType: statusItem.operationType,
        createdDateTime: statusItem.createdDateTime,
        lastActionDateTime: statusItem.lastActionDateTime
    };
};

/**
 * Maps the api received intent faulty utterances object to an IntentTrainingResult item.
 *
 * @param intentItem The intent faulty utterances object received from the api.
 * @returns An IntentTrainingResult item.
 */
export const intentFaultyUtterancesSwaggerToIntentTrainingResult = (intentItem: IntentFaultyUtterancesSwagger): IntentTrainingResult => {
    const unclearIntentsMap = new Map<number, UtteranceTrainingResult>();
    const incorrectIntentsMap = new Map<number, UtteranceTrainingResult>();

    intentItem.utterances
        .filter(u => u.misclassifiedIntentPredictions.length === 0 && u.ambiguousIntentPredictions.length > 0)
        .forEach(u => {
            unclearIntentsMap.set(u.id, {
                utteranceId: u.id,
                unclearIntents: u.ambiguousIntentPredictions.map(i => ({ intentId: i.id, intentName: i.name, score: i.score })),
                incorrectIntents: u.misclassifiedIntentPredictions.map(i => ({ intentId: i.id, intentName: i.name, score: i.score }))
            });
        });
    intentItem.utterances
        .filter(u => u.misclassifiedIntentPredictions.length > 0)
        .forEach(u => {
            incorrectIntentsMap.set(u.id, {
                utteranceId: u.id,
                unclearIntents: u.ambiguousIntentPredictions.map(i => ({ intentId: i.id, intentName: i.name, score: i.score })),
                incorrectIntents: u.misclassifiedIntentPredictions.map(i => ({ intentId: i.id, intentName: i.name, score: i.score }))
            });
        });

    const map: Map<LuisId, [string, number]> = new Map<LuisId, [string, number]>();
    Array.from(unclearIntentsMap.values()).forEach(u => {
        if (!map.has(u.unclearIntents[0].intentId)) {
            map.set(u.unclearIntents[0].intentId, [u.unclearIntents[0].intentName, 0]);
        }
        map.set(u.unclearIntents[0].intentId, [u.unclearIntents[0].intentName, map.get(u.unclearIntents[0].intentId)[1] + 1]);
    });

    const unclearIntents = Array.from(map.entries())
        .map(e => ({ name: e[1][0], id: e[0], utterancesCount: e[1][1] }))
        .sort((a, b) => (a.utterancesCount < b.utterancesCount ? 1 : -1));

    map.clear();

    Array.from(incorrectIntentsMap.values()).forEach(u =>
        u.incorrectIntents.forEach(i => {
            if (!map.has(i.intentId)) {
                map.set(i.intentId, [i.intentName, 0]);
            }
            map.set(i.intentId, [i.intentName, map.get(i.intentId)[1] + 1]);
        })
    );

    const incorrectIntents = Array.from(map.entries())
        .map(e => ({ name: e[1][0], id: e[0], utterancesCount: e[1][1] }))
        .sort((a, b) => (a.utterancesCount < b.utterancesCount ? 1 : -1));

    return {
        incorrectWith: incorrectIntents,
        unclearWith: unclearIntents,
        unclearUtterances: unclearIntentsMap,
        incorrectUtterances: incorrectIntentsMap
    };
};

/**
 * Maps the api received endpoint hits object to a map date/endpoint hits count item.
 *
 * @param hits The endpoint hits object received from the api.
 * @returns A map date/endpoint hits count item.
 */
export const endpointHitsSwaggerToEndpointMap = (hits: EndpointHitsSwagger): Map<string, number> => {
    const endpointsMap: Map<string, number> = new Map<string, number>();
    Object.keys(hits).map(k => endpointsMap.set(k, hits[k]));

    return endpointsMap;
};
